import { useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { BulletPanel } from '../../components/features/idt-kpi/BulletPanel.js';
import { CaseTable } from '../../components/features/idt-kpi/CaseTable.js';
import { DesignEvaluationCard } from '../../components/features/idt-kpi/DesignEvaluationCard.js';
import { DesignerKarteRow } from '../../components/features/idt-kpi/DesignerKarteRow.js';
import { DonutBlock } from '../../components/features/idt-kpi/DonutBlock.js';
import { FilterChips } from '../../components/features/idt-kpi/FilterChips.js';
import { HistoryTrendChart } from '../../components/features/idt-kpi/HistoryTrendChart.js';
import { KarteModal } from '../../components/features/idt-kpi/KarteModal.js';
import { PeriodSelect } from '../../components/features/idt-kpi/PeriodSelect.js';
import { PtDetailModal } from '../../components/features/idt-kpi/PtDetailModal.js';
import { TeamTrendChart } from '../../components/features/idt-kpi/TeamTrendChart.js';
import {
  aggregateQuarterDesignerKpis,
  distributeIntExact,
  toCaseRows,
  toDesignerKpis,
} from '../../components/features/idt-kpi/shared.js';
import type { KarteDesignerId, TrendView } from '../../components/features/idt-kpi/shared.js';
import type { EvalSeriesPoint } from '../../components/features/idt-kpi/designEvaluation.js';
import {
  availableMonths,
  jstCurrentMonth,
  monthLabelOf,
  nextMonth,
  prevMonth,
  quarterLabelOf,
  quarterMonthsOf,
} from '../../components/features/idt-kpi/periods.js';
import {
  useIdtActiveCases,
  useIdtDesignEvaluation,
  useIdtDesignEvaluationByDesigner,
  useIdtKpiMonths,
} from '../../hooks/useIdtKpi.js';
import { ProtectedRoute } from '../../components/auth/index.js';
import type { IdtCategory, IdtDesignEvaluationPoint } from '../../lib/api.js';

/** karte の種別pt内訳 表示順 (DonutBlock の濃淡順と揃える)。 @graph-connects none */
const KARTE_CATEGORY_ORDER: IdtCategory[] = [
  'デザイン',
  '情報設計',
  'デザインチェック',
  'マスターデザインマージ',
];

/** bullet chart の bar クリックで開く pt 明細モーダルの対象。 @graph-connects none */
type PtDetailTarget = { kind: 'designer'; id: string } | { kind: 'team' } | null;

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business IDT KPIページ。現役デザイナーの月次KPI進捗(bullet)、チーム予定vs実績(折れ線)、案件明細+構成比(ドーナツ)、karte 対象デザイナーの karte をフィルタ連動で表示する。member管理配下のサブページ
 * @graph-connects none
 */
export const Route = createFileRoute('/members/idt-kpi')({
  component: IdtKpiPage,
});

/** @graph-connects ProtectedRoute [calls] IDチーム看板のアクセス可否（idt/pmot/admin/designer）でガードする */
function IdtKpiPage() {
  return (
    <ProtectedRoute pageKey="idtKpi">
      <IdtKpiPageContent />
    </ProtectedRoute>
  );
}

/** @graph-connects none */
function IdtKpiPageContent() {
  const { t } = useTranslation();
  const [trendView, setTrendView] = useState<TrendView>('month');
  const current = jstCurrentMonth();
  // 月 view / Q view それぞれの選択をフォーカス別に保持する (view 切替で記憶を保つ)
  const [selectedMonth, setSelectedMonth] = useState(current);
  const [selectedQuarterMonth, setSelectedQuarterMonth] = useState(current);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [openKarteId, setOpenKarteId] = useState<KarteDesignerId | null>(null);
  // bullet chart の bar クリックで開く pt 明細モーダルの対象 (designer 個人 or 全体)。
  // MyPage 月間pt カードと同じ気泡モーダルを、看板の選択期間 scoped な cases で開く。
  const [ptDetailTarget, setPtDetailTarget] = useState<PtDetailTarget>(null);

  // 月 view は 1 ヶ月、Q view は Q の 3 ヶ月を取得する
  const months = useMemo(
    () => (trendView === 'month' ? [selectedMonth] : quarterMonthsOf(selectedQuarterMonth)),
    [trendView, selectedMonth, selectedQuarterMonth],
  );
  const results = useIdtKpiMonths(months);

  // 担当案件明細テーブル: 選択中の期間ビューに依らず、未完了かつ直近 1 ヶ月以内に更新のある
  // 案件を表示する (KPI の月次 cases とは別クエリで一度だけ取得。MyPage 担当案件一覧と同じ期間非依存)。
  const activeCases = useIdtActiveCases();

  // 履歴トレンド (ページ下部): 最古月〜当月 + 未来1ヶ月 を取得する。未来月は x 軸に枠だけ
  // 残し、データがあれば表示する。上段と queryKey (['idt-kpi', m]) を共有しキャッシュ再利用。
  const historyMonths = useMemo(() => [...availableMonths(current), nextMonth(current)], [current]);
  const historyResults = useIdtKpiMonths(historyMonths);
  const historyDatas = useMemo(() => historyResults.map((r) => r.data), [historyResults]);

  // デザイン評価 (全体) は月パラメータに依らない全期間系列 (月次 + 四半期)。看板下部の評価カードで使う。
  const designEval = useIdtDesignEvaluation();
  const designEvalPoints = designEval.data?.points ?? [];
  const designEvalQuarterPoints = designEval.data?.quarterPoints ?? [];
  // デザイナー個人別の月次評価 (karte モーダルの前月 個人 vs 全体 比較用)。
  const designEvalByDesigner = useIdtDesignEvaluationByDesigner();

  // 過去 Q (Q 末月 < 当月) は「完了のみ」を表示する。当 Q は完了+処理中のまま。
  // 下段テーブルは非 past-quarter でのみ activeCases を消費する (past-quarter は月次
  // caseRows を使う) ため、その門制判定に使えるよう loading/error 判定より前に確定させる。
  const isPastQuarter = useMemo(() => {
    if (trendView !== 'quarter') return false;
    const ms = quarterMonthsOf(selectedQuarterMonth);
    const last = ms[ms.length - 1];
    return last != null && last < current;
  }, [trendView, selectedQuarterMonth, current]);

  // activeCases はそれを実際に描画するビュー (非 past-quarter) でのみページ全体の
  // loading/error 門制に含める。無関係な past-quarter 履歴ビューが active-cases の
  // 失敗/ロードで全画面 error/loading にならないようにする。
  const gateActiveCases = !isPastQuarter;
  const isLoading = results.some((r) => r.isLoading) || (gateActiveCases && activeCases.isLoading);
  const isError = results.some((r) => r.isError) || (gateActiveCases && activeCases.isError);
  const error =
    results.find((r) => r.isError)?.error ??
    (gateActiveCases && activeCases.isError ? activeCases.error : undefined);
  const datas = useMemo(() => results.map((r) => r.data), [results]);
  // trend / asOfDate / quarterMonths の代表レスポンス。
  // quarterTrendDaily の当月 capacity は「リクエスト月 === 当月」のときだけ動的に入るため、
  // 取得月に当月が含まれる場合は当月のレスポンスを代表にする (Q view で当月区間が
  // flat にならないようにする)。含まれない過去 Q は全月 override/静的で解決済みなので末尾を使う。
  const primary = useMemo(() => {
    const idx = months.indexOf(current);
    return idx >= 0 ? datas[idx] : datas[datas.length - 1];
  }, [datas, months, current]);

  const completedByDesigner = primary?.team.quarterCompletedByDesigner ?? {};

  // 左 KPI バレット: 月 view は当月、Q view は 3 ヶ月の designer 別合算。
  // 過去 Q は actual を「完了のみ」に差し替える。
  const kpiData = useMemo(() => {
    if (trendView === 'month') return toDesignerKpis(primary);
    const agg = aggregateQuarterDesignerKpis(datas);
    if (!isPastQuarter) return agg;
    // 過去 Q は完了のみ: actual を完了に差替え、処理中は 0 にする。
    // Q 総目標 override がある静的 Q は、個人別目標を override 合計に占比按分する (整数・合計厳密
    // 一致)。バレット総目標が override = Q view 総目標と一致するようにする。
    const targetOverride = primary?.team.quarterTargetOverride ?? null;
    const targets =
      targetOverride != null
        ? distributeIntExact(
            targetOverride,
            agg.map((d) => d.monthlyTarget),
          )
        : null;
    return agg.map((d, i) => ({
      ...d,
      actual: Math.round(completedByDesigner[d.id] ?? 0),
      inProgress: 0,
      ...(targets != null
        ? { monthlyTarget: targets[i] ?? 0, currentTarget: targets[i] ?? 0 }
        : {}),
    }));
  }, [trendView, primary, datas, isPastQuarter, completedByDesigner]);
  // 期間 scoped の cases (当月 / Q の 3 ヶ月分)。karte の当期 種別pt 内訳と、過去 Q の
  // 完了のみ履歴ビューに使う。下段テーブルの通常ビューには使わない (activeCaseRows を使う)。
  const caseRows = useMemo(() => {
    const rows = trendView === 'month' ? toCaseRows(primary) : datas.flatMap((d) => toCaseRows(d));
    // 過去 Q は完了案件のみの履歴ビュー。
    return isPastQuarter ? rows.filter((c) => c.status === '完了') : rows;
  }, [trendView, primary, datas, isPastQuarter]);
  // 担当案件明細テーブル: 過去 Q は完了のみの履歴ビュー (期間 scoped)、それ以外は選択期間に
  // 依らない未完了案件一覧 (activeCases)。未完了行の除外 +「完了にした行のスライドアウト演出」は
  // CaseTable 側 (hideCompleted) で行う。
  const activeCaseRows = useMemo(() => toCaseRows(activeCases.data), [activeCases.data]);
  const tableRows = isPastQuarter ? caseRows : activeCaseRows;
  const teamBreakdown = useMemo(() => {
    if (isPastQuarter) {
      const completed = Object.values(completedByDesigner).reduce((s, v) => s + v, 0);
      return { completed: Math.round(completed), inProgress: 0 };
    }
    return datas.reduce(
      (acc, d) => ({
        completed: acc.completed + (d?.team.totalActualCompleted ?? 0),
        inProgress: acc.inProgress + (d?.team.totalActualInProgress ?? 0),
      }),
      { completed: 0, inProgress: 0 },
    );
  }, [datas, isPastQuarter, completedByDesigner]);
  const total = useMemo(() => kpiData.reduce((s, d) => s + d.actual, 0), [kpiData]);
  const filteredCases = activeFilter
    ? tableRows.filter((c) => c.owner === activeFilter)
    : tableRows;

  // 開いている karte の派生データ: 前月の個人/全体評価 (radar) と当期の種別pt内訳。
  const karteData = useMemo(() => {
    if (!openKarteId) return null;
    const targetMonth = prevMonth(primary?.month ?? selectedMonth);
    const toEval = (p: IdtDesignEvaluationPoint | undefined): EvalSeriesPoint | null =>
      p
        ? {
            period: p.month,
            resolution: p.resolution,
            evaluation: p.evaluation,
            evaluationCounts: p.evaluationCounts,
          }
        : null;
    const selfPoint = designEvalByDesigner.data?.designers
      .find((d) => d.nickname === openKarteId)
      ?.points.find((p) => p.month === targetMonth);
    const overallPoint = designEvalPoints.find((p) => p.month === targetMonth);
    const categoryPt = KARTE_CATEGORY_ORDER.map((category) => ({
      category,
      pt:
        Math.round(
          caseRows
            .filter((c) => c.owner === openKarteId && c.category === category)
            .reduce((s, c) => s + c.actual, 0) * 10,
        ) / 10,
    }));
    return {
      evalSelf: toEval(selfPoint),
      evalOverall: toEval(overallPoint),
      evalMonth: targetMonth,
      evalMonthLabel: monthLabelOf(targetMonth),
      categoryPt,
    };
  }, [openKarteId, primary, selectedMonth, designEvalByDesigner.data, designEvalPoints, caseRows]);

  const periodValue = trendView === 'month' ? selectedMonth : selectedQuarterMonth;
  const onPeriodChange = (next: string) => {
    if (trendView === 'month') setSelectedMonth(next);
    else setSelectedQuarterMonth(next);
  };

  // pt 明細モーダル: bullet と同じ期間 scoped な caseRows を対象者で絞る
  // (全体 bar は絞らない)。過去 Q は caseRows 自体が完了のみなので注記を差し替える。
  const periodLabel =
    trendView === 'month' ? monthLabelOf(selectedMonth) : quarterLabelOf(selectedQuarterMonth);
  const ptDetailCases =
    ptDetailTarget?.kind === 'designer'
      ? caseRows.filter((c) => c.owner === ptDetailTarget.id)
      : caseRows;
  const ptDetailTitle =
    ptDetailTarget?.kind === 'designer'
      ? t('idtKpi.ptDetail.title', {
          owner: ptDetailTarget.id,
          period: periodLabel,
          defaultValue: `${ptDetailTarget.id}・${periodLabel} の pt 明細`,
        })
      : t('idtKpi.ptDetail.teamTitle', {
          period: periodLabel,
          defaultValue: `全体・${periodLabel} の pt 明細`,
        });

  if (isLoading) {
    return (
      <div className="p-8 text-center text-sm text-[var(--color-text-muted)] animate-fade-in">
        Loading…
      </div>
    );
  }
  if (isError) {
    return (
      <div className="p-8 text-center text-sm text-[var(--color-error)] animate-fade-in">
        {error instanceof Error ? error.message : String(error)}
      </div>
    );
  }
  if (!primary) return null;

  return (
    <div className="space-y-6 animate-fade-in">
      {primary.localDryRunMode && (
        <div
          className="px-3 py-2 rounded-md text-xs border border-[var(--color-warning)] text-[var(--color-warning)] bg-[color-mix(in_srgb,var(--color-warning)_8%,transparent)]"
          role="status"
        >
          {t(
            'idtKpi.dryRunBanner',
            'LOCAL_DRY_RUN_DEFAULT=true: 戦闘力編集は dryRun (BQ には書き込まれません)',
          )}
        </div>
      )}

      <div className="animate-content-reveal space-y-3">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            {t('idtKpi.title')}
          </h1>
          <p className="mt-1 text-[var(--color-text-secondary)]">{primary.asOfDate}</p>
        </div>
        <Link
          to="/design-search"
          className="inline-flex items-center gap-1 rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-sm text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)]"
          aria-label={t('designSearch.entryLink')}
        >
          🔍 {t('designSearch.entryLink')}
        </Link>
      </div>

      {/* Top row: bullet + line chart */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 animate-content-reveal">
        <Card>
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
                {t('idtKpi.personalProgress.title')}
              </h3>
              <p className="text-xs text-[var(--color-text-muted)] mt-1">
                {t('idtKpi.personalProgress.subtitle')}
              </p>
            </div>
          </div>
          <BulletPanel
            data={kpiData}
            teamBreakdown={teamBreakdown}
            onSelectDesigner={(id) => {
              setPtDetailTarget({ kind: 'designer', id });
            }}
            onSelectTeam={() => {
              setPtDetailTarget({ kind: 'team' });
            }}
          />
          <PtDetailModal
            cases={ptDetailCases}
            title={ptDetailTitle}
            note={
              isPastQuarter
                ? t('idtKpi.ptDetail.noteCompleted', {
                    period: periodLabel,
                    defaultValue: `${periodLabel} に完了したデザイン子案件です`,
                  })
                : t('idtKpi.ptDetail.note', {
                    period: periodLabel,
                    defaultValue: `${periodLabel} のデザイン子案件（完了 + 処理中）です`,
                  })
            }
            open={ptDetailTarget !== null}
            onClose={() => {
              setPtDetailTarget(null);
            }}
          />
        </Card>

        <Card className="flex flex-col">
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
                {t('idtKpi.teamTrend.title')}
              </h3>
              <p className="text-xs text-[var(--color-text-muted)] mt-1">
                {t(
                  trendView === 'month'
                    ? 'idtKpi.teamTrend.subtitleMonth'
                    : 'idtKpi.teamTrend.subtitleQuarter',
                )}
              </p>
            </div>
            <PeriodSelect view={trendView} value={periodValue} onChange={onPeriodChange} />
          </div>
          <TeamTrendChart
            view={trendView}
            onViewChange={setTrendView}
            trend={primary.team}
            asOfDate={primary.asOfDate}
            quarterCompletedOnly={isPastQuarter}
          />
        </Card>
      </div>

      {/* Bottom: case detail + donut + karte */}
      <Card className="animate-content-reveal">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-5">
          <div>
            <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
              {t('idtKpi.caseDetail.title')}
            </h3>
            <p className="text-xs text-[var(--color-text-muted)] mt-1">
              {t('idtKpi.caseDetail.subtitle')}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-[var(--color-text-muted)]">
              {t('idtKpi.caseDetail.filterLabel')}
            </span>
            <FilterChips active={activeFilter} onChange={setActiveFilter} />
          </div>
        </div>

        <CardContent className="mt-0 @container">
          {/* コンテナ幅基準で 2 段組み (案件表 + donut/karte) を切替える。サイドバー展開で
              カード幅が狭まると viewport の lg では足りず donut が画面外へ出るため、@container で
              実際のカード幅を見て、狭いときは 1 段に積んで donut/karte を完全表示する。
              2 段時も左トラックは minmax(0,1fr) で min-content 未満まで縮められ、案件表は内部
              スクロール、donut 列 (360px) は常に表示される。 */}
          <div className="grid grid-cols-1 gap-8 items-start @[1180px]:grid-cols-[minmax(0,1fr)_360px]">
            <div className="flex min-w-0 flex-col gap-6">
              <CaseTable
                rows={filteredCases}
                hideCompleted={!isPastQuarter}
                resetPageKey={`${trendView}:${periodValue}:${activeFilter ?? ''}`}
              />
              {/* 実績 vs 目標 履歴トレンド (compact)。担当案件明細の下・karte の左に配置 */}
              <div className="pt-2 border-t border-[var(--color-border)]">
                <h4 className="text-sm font-semibold text-[var(--color-text-primary)]">
                  {t('idtKpi.historyTrend.title')}
                </h4>
                <p className="text-xs text-[var(--color-text-muted)] mt-0.5 mb-2">
                  {t('idtKpi.historyTrend.subtitle')}
                </p>
                <HistoryTrendChart responses={historyDatas} currentMonth={current} height={190} />
              </div>
            </div>
            <div className="flex flex-col gap-6">
              <DonutBlock
                total={total}
                data={kpiData}
                active={activeFilter}
                onSelect={setActiveFilter}
              />
              <DesignerKarteRow onOpen={setOpenKarteId} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* デザイン評価: 全体の案件開始時解像度 + チーム評価 (radar 前月比較 / FY 推移折線) */}
      <DesignEvaluationCard
        points={designEvalPoints}
        quarterPoints={designEvalQuarterPoints}
        isLoading={designEval.isLoading}
      />

      {openKarteId && karteData && (
        <KarteModal
          id={openKarteId}
          month={primary.month}
          combatPower={
            primary.designers.find((d) => d.nickname.toLowerCase() === openKarteId)?.combatPower ??
            null
          }
          categoryPt={karteData.categoryPt}
          evalSelf={karteData.evalSelf}
          evalOverall={karteData.evalOverall}
          evalMonth={karteData.evalMonth}
          evalMonthLabel={karteData.evalMonthLabel}
          onClose={() => {
            setOpenKarteId(null);
          }}
        />
      )}
    </div>
  );
}
